import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import type { HeaderFilter, HeaderFilterOption } from '../../../contexts/DynamicHeaderContext';
import { GradientCheckbox } from './GradientCheckbox';

interface HierarchicalCheckboxFilterProps {
  filter: HeaderFilter;
}

export function HierarchicalCheckboxFilter({ filter }: HierarchicalCheckboxFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedValues = Array.isArray(filter.value) ? filter.value : [];
  const options = filter.options || [];

  const { rootOptions, childrenMap } = useMemo(() => {
    const childrenMap = new Map<string | null, HeaderFilterOption[]>();
    options.forEach(opt => {
      const parentId = opt.parentId ?? null;
      if (!childrenMap.has(parentId)) childrenMap.set(parentId, []);
      childrenMap.get(parentId)!.push(opt);
    });
    const rootOptions = childrenMap.get(null) || [];
    return { rootOptions, childrenMap };
  }, [options]);

  useEffect(() => {
    if (!searchQuery) return;
    const query = searchQuery.toLowerCase();
    const newExpanded = new Set<string>();
    options.forEach(opt => {
      if (opt.label.toLowerCase().includes(query)) {
        let currentParentId = opt.parentId;
        while (currentParentId) {
          newExpanded.add(currentParentId);
          const parent = options.find(o => o.value === currentParentId);
          currentParentId = parent?.parentId ?? null;
        }
      }
    });
    setExpandedIds(newExpanded);
  }, [searchQuery, options]);

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 280;
      const dropdownHeight = 500;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top = rect.bottom + 4;
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        top = rect.top - dropdownHeight - 4;
      }

      const spaceRight = window.innerWidth - rect.left;
      let left = rect.left;
      if (spaceRight < dropdownWidth + 16) left = rect.right - dropdownWidth;
      left = Math.max(8, Math.min(left, window.innerWidth - dropdownWidth - 8));
      setPosition({ top: Math.max(8, top), left });
    }
  };

  const handleToggle = () => {
    if (!isOpen) updatePosition();
    setIsOpen(!isOpen);
    setSearchQuery('');
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) setIsOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
      return newSet;
    });
  };

  const getDescendantIds = (parentId: string): string[] => {
    const children = childrenMap.get(parentId) || [];
    const ids: string[] = [];
    children.forEach(child => { ids.push(child.value); ids.push(...getDescendantIds(child.value)); });
    return ids;
  };

  const toggleValue = (value: string) => {
    const isSelected = selectedValues.includes(value);
    const descendantIds = getDescendantIds(value);
    if (isSelected) {
      const idsToRemove = new Set([value, ...descendantIds]);
      filter.onChange(selectedValues.filter(v => !idsToRemove.has(v)));
    } else {
      filter.onChange([...new Set([...selectedValues, value, ...descendantIds])]);
    }
  };

  const selectAll = () => filter.onChange(options.map(o => o.value));
  const clearAll = () => filter.onChange([]);

  const matchesSearch = (opt: HeaderFilterOption): boolean => {
    if (!searchQuery) return true;
    return opt.label.toLowerCase().includes(searchQuery.toLowerCase());
  };

  const hasMatchingDescendant = (opt: HeaderFilterOption): boolean => {
    if (matchesSearch(opt)) return true;
    const children = childrenMap.get(opt.value) || [];
    return children.some(child => hasMatchingDescendant(child));
  };

  const countSelectedChildren = (parentId: string): { selected: number; total: number } => {
    const children = childrenMap.get(parentId) || [];
    let selected = 0;
    let total = children.length;
    children.forEach(child => {
      if (selectedValues.includes(child.value)) selected++;
      const grandChildCounts = countSelectedChildren(child.value);
      selected += grandChildCounts.selected;
      total += grandChildCounts.total;
    });
    return { selected, total };
  };

  const getButtonLabel = () => {
    if (selectedValues.length === 0) return filter.label;
    if (selectedValues.length === 1) {
      const opt = options.find(o => o.value === selectedValues[0]);
      return opt?.label || selectedValues[0];
    }
    if (selectedValues.length === options.length) return `${filter.label}: Tutti`;
    return `${filter.label}: ${selectedValues.length}`;
  };

  const renderOption = (option: HeaderFilterOption, level: number = 0) => {
    const children = childrenMap.get(option.value) || [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedIds.has(option.value);
    const isSelected = selectedValues.includes(option.value);

    if (searchQuery && !hasMatchingDescendant(option)) return null;

    const childCounts = hasChildren ? countSelectedChildren(option.value) : null;

    return (
      <div key={option.value}>
        <div
          className="flex items-center gap-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-white/10 cursor-pointer"
          style={{ paddingLeft: `${8 + level * 16}px`, paddingRight: '8px' }}
        >
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            {hasChildren ? (
              <button
                onClick={(e) => toggleExpand(option.value, e)}
                className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {isExpanded
                  ? <ChevronDown className="w-3 h-3 text-gray-500" />
                  : <ChevronRight className="w-3 h-3 text-gray-500" />
                }
              </button>
            ) : null}
          </div>

          <div
            onClick={(e) => { e.stopPropagation(); toggleValue(option.value); }}
            className="cursor-pointer"
          >
            <GradientCheckbox checked={isSelected} />
          </div>

          {option.color && (
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: option.color }} />
          )}

          <span
            onClick={(e) => { e.stopPropagation(); toggleValue(option.value); }}
            className={`flex-1 truncate cursor-pointer ${
              level === 0
                ? 'text-sm font-medium text-gray-700 dark:text-gray-200'
                : 'text-sm text-gray-600 dark:text-gray-300'
            }`}
          >
            {option.label}
          </span>

          {hasChildren && childCounts && childCounts.selected > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-pink-50 dark:bg-pink-900/20 text-[#E91E8C]">
              {childCounts.selected}/{childCounts.total}
            </span>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div>{children.map(child => renderOption(child, level + 1))}</div>
        )}
      </div>
    );
  };

  const hasSelection = selectedValues.length > 0;

  const gradientTextStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #E91E8C, #F97316)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`inline-flex items-center gap-1.5 px-2.5 h-7 text-xs rounded-md focus:outline-none transition-colors ${
          hasSelection
            ? 'bg-[rgba(233,30,140,0.08)] hover:bg-[rgba(233,30,140,0.13)]'
            : 'text-foreground hover:bg-black/5 dark:hover:bg-white/5'
        }`}
      >
        <span className="truncate max-w-[100px]" style={hasSelection ? gradientTextStyle : {}}>
          {getButtonLabel()}
        </span>
        <ChevronDown
          className={`w-3 h-3 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''} ${hasSelection ? 'text-[#E91E8C]' : 'text-muted-foreground'}`}
        />
      </button>

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-72 max-h-[500px] rounded-lg shadow-xl bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-white/10 z-[20050] flex flex-col"
          style={{ top: position.top, left: position.left }}
        >
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            {filter.searchable && (
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca..."
                  className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#E91E8C]"
                  autoFocus
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center justify-between text-xs">
              <button onClick={selectAll} className="hover:underline" style={gradientTextStyle}>
                Seleziona tutti
              </button>
              <button onClick={clearAll} className="text-muted-foreground hover:underline">
                Deseleziona tutti
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto py-1">
            {rootOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">Nessuna opzione disponibile</div>
            ) : (
              rootOptions.map(option => renderOption(option, 0))
            )}
          </div>

          <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
            {selectedValues.length} di {options.length} selezionati
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
